import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { catchError, of, Subscription } from 'rxjs';
import { Header } from '../../components/shared/header/header';
import { DropdownTable } from '../../components/shared/dropdown-table/dropdown-table';
import { LeadColumn, LeadRow, FilterConfig, ServerSidePagination, ViewButtonTracking } from '../../components/shared/leads-table/leads-table';
import { LeadsFilaService } from '../../services/leads-fila.service';
import { LeadsFilaItem, LeadsFilaRequest } from '../../interfaces/leads-fila.interface';
import { SelectedDnService } from '../../services/selected-dn.service';
import { FilterFieldsUtil } from '../../utils/filter-fields-utils';
import { AdminCampaignService } from '../../services/admin-campaign.service';
import { AdminCampaignApiItem } from '../../interfaces/admin-campaign.interface';

interface CampaignGroupState {
  id: string;
  title: string;
  subtitle: string;
  isOpen: boolean;
  hasLoaded: boolean;
  loading: boolean;
  errorMessage: string;
  emptyMessage: string;
  tableData: LeadRow[];
  serverSidePagination?: ServerSidePagination;
  campaignCode?: string;
  filters: {
    searchText?: string;
    selectedFilters: string[];
    dateRange: { start: string; end: string };
    sortField: string;
    sortOrder: 'ASC' | 'DESC';
  };
}

@Component({
  selector: 'app-special-campaigns',
  standalone: true,
  imports: [CommonModule, Header, DropdownTable],
  templateUrl: './special-campaigns.html',
  styleUrls: ['./special-campaigns.scss']
})
export class SpecialCampaigns implements OnInit, OnDestroy {
  private readonly tipoCampanha = 4;
  private dnSubscription?: Subscription;

  tableColumns: LeadColumn[] = [
    { key: 'nome', label: 'Nome', type: 'text' },
    { key: 'dataTerminoContrato', label: 'Data término de contrato', type: 'text' },
    { key: 'statusContrato', label: 'Status do contrato', type: 'text' },
    { key: 'disponivelDesde', label: 'Disponível desde', type: 'text' },
    { key: 'preAprovacao', label: 'Pré Aprovação', type: 'text' },
    { key: 'retornarEm', label: 'Retornar em', type: 'text' },
    { key: 'status', label: 'Status do Lead', type: 'status' }
  ];

  filterConfig: FilterConfig = {
    searchFields: ['nome'],
    alphabeticalField: 'NM_NOME',
    filterFields: {
      renewal: 'renovaMais',
      preAprovado: 'preAprovacao',
      origin: 'origem'
    },
    dateField: 'dataTerminoContrato'
  };

  trackingConfig: ViewButtonTracking = {
    trackEvent: 'visualizar_campanhas_especiais',
    trackCategory: 'campanhas_especiais',
    trackLabel: 'view_campanha_especial'
  };

  groups: CampaignGroupState[] = [];

  constructor(
    private leadsFilaService: LeadsFilaService,
    private selectedDnService: SelectedDnService,
    private adminCampaignService: AdminCampaignService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.loadGroups();

    this.dnSubscription = this.selectedDnService.selectedDn$.subscribe((dn) => {
      if (!dn) {
        return;
      }

      this.groups = this.groups.map((group) => ({
        ...group,
        hasLoaded: false,
        tableData: [],
        serverSidePagination: undefined
      }));

      const firstOpenGroup = this.groups.find((group) => group.isOpen);
      if (firstOpenGroup) {
        this.loadGroupData(firstOpenGroup, 1, 20);
      }
    });
  }

  ngOnDestroy(): void {
    this.dnSubscription?.unsubscribe();
  }

  private loadGroups(): void {
    this.adminCampaignService.getCampaigns(undefined, undefined, this.tipoCampanha).pipe(
      catchError(() => of([] as AdminCampaignApiItem[]))
    ).subscribe((campaigns) => {
      const activeCampaigns = campaigns.filter((campaign) => campaign.flStatus === 'A' || campaign.ativo);

      if (activeCampaigns.length > 0) {
        this.groups = activeCampaigns.map((campaign, index) => this.createGroupState({
          id: `campaign-${campaign.idCampanha}`,
          title: campaign.dsCampanha,
          subtitle: `${this.formatDate(campaign.dtIni)} à ${this.formatDate(campaign.dtFim)} - Código ${campaign.cdCampanha}`,
          campaignCode: campaign.cdCampanha,
          isOpen: index === 0
        }));
      } else {
        this.groups = this.getFallbackGroups();
      }

      const firstOpenGroup = this.groups.find((group) => group.isOpen);
      if (firstOpenGroup) {
        this.loadGroupData(firstOpenGroup, 1, 20);
      }
    });
  }

  private createGroupState(config: {
    id: string;
    title: string;
    subtitle: string;
    campaignCode?: string;
    isOpen: boolean;
  }): CampaignGroupState {
    return {
      id: config.id,
      title: config.title,
      subtitle: config.subtitle,
      campaignCode: config.campaignCode,
      isOpen: config.isOpen,
      hasLoaded: false,
      loading: false,
      errorMessage: '',
      emptyMessage: 'Nenhum lead encontrado para a campanha informada.',
      tableData: [],
      filters: {
        searchText: undefined,
        selectedFilters: [],
        dateRange: { start: '', end: '' },
        sortField: 'DT_CONTR_FIM',
        sortOrder: 'DESC'
      }
    };
  }

  private getFallbackGroups(): CampaignGroupState[] {
    return [
      this.createGroupState({
        id: 'legacy-campaign-1',
        title: 'Lançamento T-Cross',
        subtitle: '01/09/2025 à 30/10/2025 - Clientes com contrato com modelos similares ao modelo de lançamento.',
        isOpen: true
      }),
      this.createGroupState({
        id: 'legacy-campaign-2',
        title: 'Especial Agosto',
        subtitle: '01/08/2025 à 30/09/2025 - Clientes com contrato encerrado há 3 anos e ativos com previsão de encerramento em dez/2026.',
        isOpen: false
      }),
      this.createGroupState({
        id: 'legacy-campaign-3',
        title: 'Lançamento TERA',
        subtitle: '01/07/2025 à 30/08/2025 - Clientes com contrato com modelos similares ao modelo de lançamento.',
        isOpen: false
      })
    ];
  }

  private loadGroupData(group: CampaignGroupState, page: number = 1, pageSize: number = 10): void {
    group.loading = true;
    group.errorMessage = '';
    group.emptyMessage = 'Nenhum lead encontrado para a campanha informada.';

    const request: LeadsFilaRequest = {
      tipoCampanha: this.tipoCampanha,
      dn: this.selectedDnService.getSelectedDnAsNumber(),
      codigoCampanha: group.campaignCode,
      preAprovado: FilterFieldsUtil.getPreAprovadoFromFilters(group.filters.selectedFilters),
      renovaMais: FilterFieldsUtil.getRenovaMaisFromFilters(group.filters.selectedFilters),
      origem: FilterFieldsUtil.getOrigemFromFilters(group.filters.selectedFilters),
      nome: group.filters.searchText,
      dataInicio: this.parseDate(group.filters.dateRange.start),
      dataFim: this.parseDate(group.filters.dateRange.end),
      page,
      pageSize,
      sortField: group.filters.sortField,
      sortOrder: group.filters.sortOrder
    };

    this.leadsFilaService.getLeadsFila(request).pipe(
      catchError((error) => {
        if (error.status === 401) {
          group.errorMessage = 'Não autorizado. Redirecionando...';
          setTimeout(() => this.router.navigate(['/login']), 1500);
        } else {
          group.errorMessage = 'Erro ao carregar dados da campanha.';
        }

        group.loading = false;
        group.tableData = [];
        return of(null);
      })
    ).subscribe((response) => {
      group.loading = false;
      group.hasLoaded = true;

      if (!response || !response.data || response.data.length === 0) {
        group.tableData = [];
        group.serverSidePagination = response?.pagination
          ? {
              totalRecords: response.pagination.totalRecords,
              page: response.pagination.page,
              pageSize: response.pagination.pageSize,
              from: response.pagination.from,
              to: response.pagination.to
            }
          : undefined;
        group.emptyMessage = response?.message || 'Nenhum lead encontrado para a campanha informada.';
        return;
      }

      group.tableData = response.data.map((item) => this.mapApiDataToTableData(item));
      group.serverSidePagination = {
        totalRecords: response.pagination.totalRecords,
        page: response.pagination.page,
        pageSize: response.pagination.pageSize,
        from: response.pagination.from,
        to: response.pagination.to
      };
    });
  }

  private mapApiDataToTableData(item: LeadsFilaItem): LeadRow {
    return {
      idLead: item.idLead,
      nome: item.nomeCliente || '-',
      dataTerminoContrato: this.normalizeValue(item.terminoContrato),
      statusContrato: this.normalizeValue(item.statusContrato),
      disponivelDesde: this.normalizeValue(item.disponivelDesde),
      preAprovacao: this.mapPreAprovado(item),
      retornarEm: this.normalizeValue(item.retornarEm),
      status: this.normalizeValue(item.statusLead),
      renewal: this.mapRenovaMais(item),
      origem: this.normalizeValue(item.origem)
    };
  }

  private mapPreAprovado(item: LeadsFilaItem): string {
    if (item.preAprovado === 'S') {
      return 'Sim';
    }

    if (item.preAprovado === 'N') {
      return 'Necessita Análise';
    }

    return 'Não Solicitado';
  }

  private mapRenovaMais(item: LeadsFilaItem): string {
    const flagRenovacao = this.getFlagRenovacao(item);
    const isRenovaMais = flagRenovacao !== null ? flagRenovacao : item.renovaMais === true;
    return isRenovaMais ? 'renewalBlue' : 'renewalGrey';
  }

  private getFlagRenovacao(item: LeadsFilaItem): boolean | null {
    const flag = (item as any).flag_renovacao ?? (item as any).flRenovacao ?? (item as any).flagRenovacao;

    if (flag === 'S') {
      return true;
    }

    if (flag === 'N') {
      return false;
    }

    return null;
  }

  private normalizeValue(value: unknown): string {
    if (value === null || value === undefined || value === 'null' || value === 'undefined' || value === '') {
      return '-';
    }

    return String(value);
  }

  private parseDate(dateString: string): Date | undefined {
    if (!dateString || dateString.trim() === '') {
      return undefined;
    }

    if (dateString.includes('/')) {
      const [day, month, year] = dateString.split('/');
      return new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
    }

    if (dateString.includes('-')) {
      return new Date(dateString);
    }

    return undefined;
  }

  private formatDate(value: string): string {
    if (!value) {
      return '-';
    }

    const rawDate = value.includes('T') ? value.split('T')[0] : value;

    if (!rawDate.includes('-')) {
      return rawDate;
    }

    const [year, month, day] = rawDate.split('-');
    if (!year || !month || !day) {
      return rawDate;
    }

    return `${day}/${month}/${year}`;
  }

  onGroupToggle(group: CampaignGroupState, isOpen: boolean): void {
    group.isOpen = isOpen;

    if (isOpen && !group.hasLoaded) {
      this.loadGroupData(group, 1, 20);
    }
  }

  onPageChange(group: CampaignGroupState, page: number): void {
    this.loadGroupData(group, page, group.serverSidePagination?.pageSize || 10);
  }

  onPageSizeChange(group: CampaignGroupState, pageSize: number): void {
    this.loadGroupData(group, 1, pageSize);
  }

  onSearchChange(group: CampaignGroupState, searchText: string): void {
    group.filters.searchText = searchText;
    this.loadGroupData(group, 1, group.serverSidePagination?.pageSize || 10);
  }

  onFilterChange(group: CampaignGroupState, filters: string[]): void {
    group.filters.selectedFilters = filters;
    this.loadGroupData(group, 1, group.serverSidePagination?.pageSize || 10);
  }

  onDateRangeChange(group: CampaignGroupState, dateRange: { start: string; end: string }): void {
    group.filters.dateRange = dateRange;
    this.loadGroupData(group, 1, group.serverSidePagination?.pageSize || 10);
  }

  onSortChange(group: CampaignGroupState, sort: { field: string; order: 'ASC' | 'DESC' }): void {
    group.filters.sortField = sort.field;
    group.filters.sortOrder = sort.order;
    this.loadGroupData(group, 1, group.serverSidePagination?.pageSize || 10);
  }

  onClearAllFilters(group: CampaignGroupState): void {
    group.filters.searchText = undefined;
    group.filters.selectedFilters = [];
    group.filters.dateRange = { start: '', end: '' };
    group.filters.sortField = 'DT_CONTR_FIM';
    group.filters.sortOrder = 'DESC';

    this.loadGroupData(group, 1, group.serverSidePagination?.pageSize || 10);
  }

  onViewLead(lead: LeadRow): void {
    if (lead.idLead) {
      window.scrollTo(0, 0);
      this.router.navigate([`/leads/${lead.idLead}/detalhes`]);
    }
  }
}
